import { TENANT_MODULES, TenantModule, TenantType } from './tenant-features.service';

/**
 * Pure helpers around tenant types and modules, shared by the modules settings page, the
 * platform tenant-management editor and the tenant-signup approval dialog.
 * They mirror Application.Services.TenantFeatures.TenantTypeDefaults on the server.
 */

/** One selectable organisation type: its i18n keys, icon and the modules it presets. */
export interface TenantTypeOption {
  type: TenantType;
  labelKey: string;
  hintKey: string;
  icon: string;
  modules: TenantModule[];
}

/** The four organisation types, in the order the pickers show them. */
export const TENANT_TYPE_OPTIONS: TenantTypeOption[] = [
  {
    type: TenantType.TutoringAcademy,
    labelKey: 'tenantModules.TypeTutoringAcademy',
    hintKey: 'tenantModules.TypeTutoringAcademyHint',
    icon: 'bi bi-person-video3',
    modules: ['tutoring', 'lms'],
  },
  {
    type: TenantType.School,
    labelKey: 'tenantModules.TypeSchool',
    hintKey: 'tenantModules.TypeSchoolHint',
    icon: 'bi bi-building',
    modules: ['tutoring', 'school', 'lms'],
  },
  {
    type: TenantType.TrainingProvider,
    labelKey: 'tenantModules.TypeTrainingProvider',
    hintKey: 'tenantModules.TypeTrainingProviderHint',
    icon: 'bi bi-journal-richtext',
    modules: ['lms'],
  },
  {
    type: TenantType.Mixed,
    labelKey: 'tenantModules.TypeMixed',
    hintKey: 'tenantModules.TypeMixedHint',
    icon: 'bi bi-grid-3x3-gap',
    modules: ['tutoring', 'school', 'lms'],
  },
];

/** Display metadata for each module toggle. */
export interface TenantModuleOption {
  module: TenantModule;
  labelKey: string;
  hintKey: string;
  icon: string;
}

export const TENANT_MODULE_OPTIONS: TenantModuleOption[] = [
  { module: 'tutoring', labelKey: 'tenantModules.ModuleTutoring', hintKey: 'tenantModules.ModuleTutoringHint', icon: 'bi bi-person-video3' },
  { module: 'school', labelKey: 'tenantModules.ModuleSchool', hintKey: 'tenantModules.ModuleSchoolHint', icon: 'bi bi-building' },
  { module: 'lms', labelKey: 'tenantModules.ModuleLms', hintKey: 'tenantModules.ModuleLmsHint', icon: 'bi bi-journal-richtext' },
];

/** The default modules of a tenant type. */
export function modulesForType(type: TenantType | null | undefined): TenantModule[] {
  const option = TENANT_TYPE_OPTIONS.find((o) => o.type === type);
  return option ? [...option.modules] : [...TENANT_MODULES];
}

/** i18n key of a tenant type's name. */
export function tenantTypeLabelKey(type: TenantType | null | undefined): string {
  return TENANT_TYPE_OPTIONS.find((o) => o.type === type)?.labelKey ?? 'tenantModules.TypeMixed';
}

/** Parses the stored comma string ("tutoring,school,lms") into known modules, canonical order. */
export function parseModulesCsv(csv: string | null | undefined): TenantModule[] {
  if (!csv) return [];
  const tokens = new Set(csv.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean));
  return TENANT_MODULES.filter((m) => tokens.has(m));
}

/** Canonical order + de-duplication for a module list. */
export function sortModules(modules: Iterable<string>): TenantModule[] {
  const set = new Set([...modules].map((m) => String(m).toLowerCase()));
  return TENANT_MODULES.filter((m) => set.has(m));
}

/**
 * The type and modules the server would pick for a sign-up's free-text organisation type
 * (same keyword rules and order as TenantTypeDefaults.For). Used to pre-fill the approval dialog.
 */
export function defaultsForOrgType(orgType: string | null | undefined): { type: TenantType; modules: TenantModule[] } {
  const t = (orgType ?? '').trim().toLowerCase();
  const has = (...needles: string[]) => needles.some((n) => t.includes(n));
  if (!t) return { type: TenantType.Mixed, modules: modulesForType(TenantType.Mixed) };
  if (has('corporate', 'bootcamp', 'training', 'trainer', 'شركات', 'تدريب')) {
    return { type: TenantType.TrainingProvider, modules: modulesForType(TenantType.TrainingProvider) };
  }
  if (has('tutor', 'academy', 'lesson', 'خصوصي', 'أكاديمية', 'اكاديمية')) {
    return { type: TenantType.TutoringAcademy, modules: modulesForType(TenantType.TutoringAcademy) };
  }
  if (has('k-12', 'k12', 'school', 'university', 'college', 'مدرسة', 'جامعة')) {
    return { type: TenantType.School, modules: modulesForType(TenantType.School) };
  }
  return { type: TenantType.Mixed, modules: modulesForType(TenantType.Mixed) };
}
